<script setup lang="ts">
import type { ShallowRef } from 'vue';
import type { SubmitEventPromise } from 'vuetify';
import type { ReadonlyDeep } from 'type-fest';
import type { ValidData as NewAirRoadUnpackDialogFormValidData } from '@/components/dialogForms/NewAirRoadUnpackDialogForm.vue';
import type {
  InitialData as NewAirRoadAddExtraProductFormInitialData,
  ValidData as NewAirRoadAddExtraProductFormValidData,
} from '@/components/forms/NewAirRoadAddExtraProductForm.vue';
import type {
  InitialData as NewAirRoadPackageTableFormInitialData,
  ValidData as NewAirRoadPackageTableFormValidData,
} from '@/components/forms/NewAirRoadPackageTableForm.vue';
import type { DialogName } from '@/stores/useDialogStore';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { PackageType } from '@/constants/airRoad';

import { computed, ref, useCssModule, useTemplateRef } from 'vue';
import _cloneDeep from 'lodash/cloneDeep';

import TotalPackageStepNewAirRoadCustomTable from '@/components/customTables/TotalPackageStepNewAirRoadCustomTable.vue';
import NewAirRoadAddExtraProductForm from '@/components/forms/NewAirRoadAddExtraProductForm.vue';
import NewAirRoadPackageTableForm from '@/components/forms/NewAirRoadPackageTableForm.vue';
import H3LineNewAirRoadHeader from '@/components/headers/H3LineNewAirRoadHeader.vue';
import StepNewAirRoadHeader from '@/components/headers/StepNewAirRoadHeader.vue';
import NewAirRoadPackagesBasicListItem from '@/components/items/NewAirRoadPackagesBasicListItem.vue';
import NewAirRoadUnpackedProductsBasicListItem from '@/components/items/NewAirRoadUnpackedProductsBasicListItem.vue';
import NewAirRoadPackAllProductsMenuList from '@/components/menuLists/NewAirRoadPackAllProductsMenuList.vue';
import PreviewDiagramNewAirRoadSection from '@/components/sections/PreviewDiagramNewAirRoadSection.vue';
import KInfoWrapper from '@/components/wrappers/KInfoWrapper.vue';
import { useDialogStore } from '@/stores/useDialogStore';
import * as airRoadHelpers from '@/helpers/airRoad';
import * as totalPackageStepNewAirRoadTableHelpers from '@/helpers/tables/totalPackageStepNewAirRoad';
import * as airRoadConstants from '@/constants/airRoad';

export interface Props {
  airRoadBookingJob?: ReadonlyDeep<openapi.AirroadJobDetailedContentOpenapi>;
}
const props = withDefaults(defineProps<Props>(), {
  airRoadBookingJob: undefined,
});

const model = defineModel<{
  params: openapi.AirroadSingleCreateQueryOpenapi;
  data: openapi.AirroadSingleCreateBodyOpenapi;
  _extraProducts: openapi.AirroadJobDetailedContentOpenapi['stops'][0]['pickup_products'];
}>({
  default: () => _cloneDeep(airRoadConstants.defaultBookingState),
});

const dialogStore = useDialogStore();

const unpackingPackageNum = ref<number>();
const unpackingProductNum = ref<number>();
const unpackingMaxQuantity = ref<number>();
const allUnpackCount = ref<number>();
const addProductId = ref<number>();

const styles = useCssModule();

function openUnpackDialog() {
  const dialogName: DialogName = 'newAirRoadUnpack';
  dialogStore.open(dialogName, {
    confirm: unpackDialogConfirm,
    packageNum: unpackingPackageNum.value,
    productNum: unpackingProductNum.value,
    allUnpackCount: allUnpackCount.value,
    addProductId: addProductId.value,
    maxQuantity: unpackingMaxQuantity.value,
  });
}

const allLegProducts = computed(function () {
  return [
    ...(props.airRoadBookingJob?.stops[model.value.data.ar_booked_leg - 1]
      ?.pickup_products || []),
    ...model.value._extraProducts,
  ];
});

const unpackedLegProducts = computed(function () {
  const _unpackedLegProducts: openapi.AirroadJobDetailedContentOpenapi['stops'][0]['pickup_products'] =
    [];
  allLegProducts.value.forEach(function (unpackedLegProduct) {
    if (!unpackedLegProduct.quantity) return;
    let packedQuantity = 0;
    model.value.data.items.forEach(function (item) {
      if (!item.products) return;
      item.products.forEach(function (product) {
        if (product.product_id !== unpackedLegProduct._id) return;
        if (!product.quantity) return;
        packedQuantity = packedQuantity + product.quantity;
      });
    });
    const leftQuantity = unpackedLegProduct.quantity - packedQuantity;
    if (leftQuantity > 0) {
      _unpackedLegProducts.push({
        ...unpackedLegProduct,
        quantity: leftQuantity,
      });
    }
  });
  return _unpackedLegProducts;
});

function handlePackAllProductsMenuListBtnClick(
  event: Event,
  packageType: PackageType,
): void {
  unpackedLegProducts.value.forEach(function (oneUnpackedLegProduct) {
    model.value.data.items.push({
      // @ts-expect-error
      item_type: packageType, // TODO 1=carton, 2=pallet
      volume: oneUnpackedLegProduct.volume,
      weight: oneUnpackedLegProduct.weight * oneUnpackedLegProduct.quantity,
      products: [
        {
          product_id: oneUnpackedLegProduct._id,
          quantity: oneUnpackedLegProduct.quantity,
          extra: !!model.value._extraProducts.find(
            (item) => item._id === oneUnpackedLegProduct._id,
          ),
        },
      ],
    });
  });
}

const cartonPackingLegProduct =
  ref<
    ReadonlyDeep<
      openapi.AirroadJobDetailedContentOpenapi['stops'][0]['pickup_products'][0]
    >
  >();
const palletPackingLegProduct =
  ref<
    ReadonlyDeep<
      openapi.AirroadJobDetailedContentOpenapi['stops'][0]['pickup_products'][0]
    >
  >();

const dropCartonEl = useTemplateRef('dropCarton');
const dropPalletEl = useTemplateRef('dropPallet');
const isDragging = ref(false);

function removeCartonPackingLegProduct(): void {
  cartonPackingLegProduct.value = undefined;
}

function removePalletPackingLegProduct(): void {
  palletPackingLegProduct.value = undefined;
}

function handleDragstart(event: DragEvent, productId: number): void {
  if (!event.dataTransfer) return;
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (productId == null || Number.isNaN(productId)) return;
  event.dataTransfer.setData('application/my-app', productId.toString());
  isDragging.value = true;
}

function handleDragend(event: DragEvent, productId: number): void {
  isDragging.value = false;
}

function handleTableDragover(event: DragEvent, packageType: PackageType): void {
  let dropTableEl;
  if (packageType === 'carton') {
    dropTableEl = dropCartonEl;
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  } else if (packageType === 'pallet') {
    dropTableEl = dropPalletEl;
  }
  if (!dropTableEl?.value) return;
  (dropTableEl.value as HTMLDivElement).classList.add(styles.dragover);
}

function handleTableDragleave(
  event: DragEvent,
  packageType: PackageType,
): void {
  let dropTableEl;
  if (packageType === 'carton') {
    dropTableEl = dropCartonEl;
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  } else if (packageType === 'pallet') {
    dropTableEl = dropPalletEl;
  }
  if (!dropTableEl?.value) return;
  (dropTableEl.value as HTMLDivElement).classList.remove(styles.dragover);
}

function handleTableDrop(event: DragEvent, packageType: PackageType): void {
  if (!event.dataTransfer) return;
  const productId = parseInt(event.dataTransfer.getData('application/my-app'));
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (productId == null || Number.isNaN(productId)) return;
  const unpackedLegProduct = unpackedLegProducts.value.find(
    function (oneUnpackedLegProduct) {
      return productId === oneUnpackedLegProduct._id;
    },
  );
  if (!unpackedLegProduct) return;
  let dropTableEl;
  if (packageType === 'carton') {
    dropTableEl = dropCartonEl;
    cartonPackingLegProduct.value = unpackedLegProduct;
    palletPackingLegProduct.value = undefined;
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  } else if (packageType === 'pallet') {
    dropTableEl = dropPalletEl;
    cartonPackingLegProduct.value = undefined;
    palletPackingLegProduct.value = unpackedLegProduct;
  }
  if (!dropTableEl?.value) return;
  (dropTableEl.value as HTMLDivElement).classList.remove(styles.dragover);
}

function handlePackageDragover(
  event: DragEvent,
  itemEl: Readonly<ShallowRef<HTMLLIElement | null>>,
): void {
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!itemEl?.value) return;
  itemEl.value.classList.add(styles.dragover);
}

function handlePackageDragleave(
  event: DragEvent,
  itemEl: Readonly<ShallowRef<HTMLLIElement | null>>,
): void {
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!itemEl?.value) return;
  itemEl.value.classList.remove(styles.dragover);
}

function handlePackageDrop(
  event: DragEvent,
  itemEl: Readonly<ShallowRef<HTMLLIElement | null>>,
  packageNum: number,
): void {
  if (!event.dataTransfer) return;
  const productId = parseInt(event.dataTransfer.getData('application/my-app'));
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (productId == null || Number.isNaN(productId)) return;
  const unpackedLegProduct = unpackedLegProducts.value.find(
    function (oneUnpackedLegProduct) {
      return productId === oneUnpackedLegProduct._id;
    },
  );
  if (!unpackedLegProduct) return;

  unpackingPackageNum.value = packageNum;
  unpackingProductNum.value = undefined;
  addProductId.value = unpackedLegProduct._id;
  allUnpackCount.value = undefined;
  unpackingMaxQuantity.value = unpackedLegProduct.quantity;
  openUnpackDialog();

  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!itemEl?.value) return;
  itemEl.value.classList.remove(styles.dragover);
}

function handlePackageSubmit(
  formData: NewAirRoadPackageTableFormValidData,
  packingLegProduct:
    | ReadonlyDeep<
        openapi.AirroadJobDetailedContentOpenapi['stops'][0]['pickup_products'][0]
      >
    | undefined,
  packageType: PackageType,
): void {
  if (!packingLegProduct) return;
  if (formData.splitCount < 1) return;
  if (formData.quantity < 1) return;
  if (formData.splitCount > formData.quantity) return;

  let splitCount = formData.splitCount;
  let quantity = formData.quantity;

  [...Array(formData.splitCount).keys()].forEach(function () {
    let splitQuantity = quantity / splitCount;
    const isFloat =
      !Number.isInteger(splitQuantity) && Number.isFinite(splitQuantity);
    if (isFloat) splitQuantity = Math.ceil(splitQuantity);

    model.value.data.items.push({
      // @ts-expect-error
      item_type: packageType, // TODO 1=carton, 2=pallet
      volume: formData.volume,
      weight: (packingLegProduct.weight || 0) * splitQuantity,
      products: [
        {
          product_id: packingLegProduct._id,
          quantity: splitQuantity,
          extra: !!model.value._extraProducts.find(
            (item) => item._id === packingLegProduct._id,
          ),
        },
      ],
    });

    splitCount = splitCount - 1;
    quantity = quantity - splitQuantity;
  });

  if (packageType === 'carton') {
    cartonPackingLegProduct.value = undefined;
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  } else if (packageType === 'pallet') {
    palletPackingLegProduct.value = undefined;
  }
}

function _getInitialData(
  packingLegProduct?: ReadonlyDeep<
    openapi.AirroadJobDetailedContentOpenapi['stops'][0]['pickup_products'][0]
  >,
): NewAirRoadPackageTableFormInitialData {
  if (!packingLegProduct) return {};
  return {
    quantity: packingLegProduct.quantity,
  };
}

const initialCartonData = computed<NewAirRoadPackageTableFormInitialData>(
  function () {
    return _getInitialData(cartonPackingLegProduct.value);
  },
);

const initialPalletData = computed<NewAirRoadPackageTableFormInitialData>(
  function () {
    return _getInitialData(palletPackingLegProduct.value);
  },
);

const addExtraProductFormInitialData: NewAirRoadAddExtraProductFormInitialData =
  {
    model: 'Extra product',
    volume: 0.048,
    weight: 0.1,
  };

function handleAddExtraProductFormSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: NewAirRoadAddExtraProductFormValidData,
): void {
  model.value._extraProducts.push({
    // TODO maybe use string prefix and date for randomness
    _id: Math.floor(100000 + Math.random() * 900000),
    model: formData.model,
    serial: '',
    description: '',
    quantity: 1,
    goods_code: '',
    volume: formData.volume,
    weight: formData.weight,
    width: 40,
    height: 40,
    length: 30,
    quant_id: NaN,
    from_stop: NaN,
    to_stop: NaN,
  });
}

function handlePackageUnpack(packageNum: number, productNum?: number): void {
  const productsPackage = model.value.data.items[packageNum - 1];
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!productsPackage) return;
  unpackingPackageNum.value = packageNum;
  unpackingProductNum.value = productNum;
  addProductId.value = undefined;
  allUnpackCount.value = undefined;
  unpackingMaxQuantity.value = undefined;
  if (productNum == null) {
    allUnpackCount.value = productsPackage.products?.length;
    openUnpackDialog();
  } else {
    if (!productsPackage.products) return;
    const product = productsPackage.products[productNum - 1];
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!product) return;
    unpackingMaxQuantity.value = product.quantity;
    openUnpackDialog();
  }
}

async function unpackDialogConfirm(
  packageNum: number,
  productNum?: number,
  addProductId?: number,
  formData?: NewAirRoadUnpackDialogFormValidData,
): Promise<void> {
  if (formData?.quantity != null) {
    let productsPackage:
      | openapi.AirroadSingleCreateBodyOpenapi['items'][0]
      | undefined;
    productsPackage = model.value.data.items[packageNum - 1];
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!productsPackage) return;
    if (!productsPackage.products) return;
    const defaultWeight = airRoadHelpers.getDefaultWeight(
      productsPackage.products,
      allLegProducts.value,
    );
    const isTotalWeightChanged = defaultWeight !== productsPackage.weight;
    if (addProductId) {
      const unpackedLegProduct = unpackedLegProducts.value.find(
        function (oneUnpackedLegProduct) {
          return addProductId === oneUnpackedLegProduct._id;
        },
      );
      if (!unpackedLegProduct) return;
      const modelProduct = productsPackage.products.find(
        function (oneModelProduct) {
          return unpackedLegProduct._id === oneModelProduct.product_id;
        },
      );
      if (modelProduct) {
        modelProduct.quantity =
          (modelProduct.quantity || 0) + formData.quantity;
      } else {
        productsPackage.products.push({
          product_id: unpackedLegProduct._id,
          quantity: formData.quantity,
          extra: !!model.value._extraProducts.find(
            (item) => item._id === unpackedLegProduct._id,
          ),
        });
      }
    } else {
      if (productNum == null) return;
      const modelProduct = productsPackage.products[productNum - 1];
      // TODO
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!modelProduct) return;
      const newQuantity = (modelProduct.quantity || 0) - formData.quantity;
      if (newQuantity > 0) {
        modelProduct.quantity = newQuantity;
      } else {
        productsPackage.products.splice(productNum - 1, 1);
        if (!productsPackage.products.length) {
          model.value.data.items.splice(packageNum - 1, 1);
          productsPackage = undefined;
        }
      }
    }
    if (productsPackage && !isTotalWeightChanged) {
      productsPackage.weight = airRoadHelpers.getDefaultWeight(
        productsPackage.products,
        allLegProducts.value,
      );
    }
  } else {
    model.value.data.items.splice(packageNum - 1, 1);
  }
}

const packageNewAirRoadStepTotalTableData = computed(function () {
  const cartonPackages: openapi.AirroadSingleCreateBodyOpenapi['items'] = [];
  const palletPackages: openapi.AirroadSingleCreateBodyOpenapi['items'] = [];
  // TODO 1=carton, 2=pallet
  model.value.data.items.forEach(function (item) {
    // @ts-expect-error
    if (item.item_type === 'carton') {
      cartonPackages.push(item);
      // @ts-expect-error
    } else if (item.item_type === 'pallet') {
      palletPackages.push(item);
    }
  });
  return {
    carton: totalPackageStepNewAirRoadTableHelpers.getTableItem(cartonPackages),
    pallet: totalPackageStepNewAirRoadTableHelpers.getTableItem(palletPackages),
  };
});

function customValidationRule(value?: unknown): boolean | string {
  if (
    packageNewAirRoadStepTotalTableData.value.carton.isCubicWeightError ||
    packageNewAirRoadStepTotalTableData.value.pallet.isCubicWeightError
  ) {
    return 'Please adjust the volume/weight to pass the error of cubic weight';
  } else if (unpackedLegProducts.value.length) {
    return 'Please pack all the products';
  } else {
    return true;
  }
}

function getRemoveExtra(productId: number): undefined | (() => void) {
  const index = model.value._extraProducts.findIndex(function (item) {
    return item._id === productId;
  });
  if (index >= 0) {
    return () => model.value._extraProducts.splice(index, 1);
  } else {
    return undefined;
  }
}
</script>

<template>
  <section
    :class="[
      $style.packageStepNewAirRoadSection,
      {
        [$style.isDragging]: isDragging,
      },
    ]"
    data-testid="packageStepNewAirRoadSection"
  >
    <StepNewAirRoadHeader>
      <h1>Job #{{ props.airRoadBookingJob?.rockhopper_id }}</h1>
    </StepNewAirRoadHeader>
    <v-text-field class="d-none" :rules="[customValidationRule]" readonly />
    <PreviewDiagramNewAirRoadSection
      :ar-stops="model.data.ar_stops"
      :job="props.airRoadBookingJob"
    />
    <div :class="$style.packaging">
      <div :class="$style.leftPackaging">
        <div :class="$style.h3AndBtns">
          <h3>Products to pack</h3>
          <NewAirRoadPackAllProductsMenuList
            @btn-click="handlePackAllProductsMenuListBtnClick"
          />
        </div>
        <p class="mt-0">
          Drag the products to Package table or use quick action icons.
        </p>
        <template v-if="unpackedLegProducts?.length">
          <ul :class="$style.productsToPackItems">
            <NewAirRoadUnpackedProductsBasicListItem
              v-for="(unpackedLegProduct, index) in unpackedLegProducts"
              :key="unpackedLegProduct._id"
              :close="getRemoveExtra(unpackedLegProduct._id)"
              :class="$style.productsToPackItem"
              :product="unpackedLegProduct"
              :draggable="true"
              @dragstart="handleDragstart($event, unpackedLegProduct._id)"
              @dragend="handleDragend($event, unpackedLegProduct._id)"
            />
          </ul>
        </template>
        <template v-else>
          <div :class="$style.emptyText">No products to pack</div>
        </template>
        <div :class="$style.addingExtraProduct">
          <KInfoWrapper>
            <template #header>
              <h4 class="m-0">Add extra product</h4>
            </template>
            <NewAirRoadAddExtraProductForm
              :initial-data="addExtraProductFormInitialData"
              @submit="handleAddExtraProductFormSubmit"
            />
          </KInfoWrapper>
        </div>
      </div>
      <div :class="$style.middlePackaging">
        <v-icon
          icon="mdi-arrow-bottom-right"
          :class="$style.middlePackagingIcon"
        ></v-icon>
      </div>
      <div :class="$style.rightPackaging">
        <h3>Package table</h3>
        <p>Pack the products in carton box or pallet and edit if required.</p>
        <div :class="$style.packageTableItems">
          <div
            ref="dropCarton"
            :class="$style.packageTableItem"
            @dragover.prevent="handleTableDragover($event, 'carton')"
            @dragleave.prevent="handleTableDragleave($event, 'carton')"
            @drop.prevent="handleTableDrop($event, 'carton')"
          >
            <div
              v-if="cartonPackingLegProduct"
              :class="$style.packageTableItemEdit"
            >
              <div :class="$style.packageTableEditItemHeader">
                <ul class="m-0 p-0">
                  <NewAirRoadUnpackedProductsBasicListItem
                    :close="removeCartonPackingLegProduct"
                    :product="cartonPackingLegProduct"
                    :is-quantity="false"
                  />
                </ul>
              </div>
              <NewAirRoadPackageTableForm
                :class="$style.packageTableForm"
                submit-text="Pack in carton box"
                :max-quantity="cartonPackingLegProduct.quantity"
                :initial-data="initialCartonData"
                @submit="
                  (event, formData) =>
                    handlePackageSubmit(
                      formData,
                      cartonPackingLegProduct,
                      'carton',
                    )
                "
              />
            </div>
            <div v-else :class="$style.packageTableItemEmpty">
              <v-icon
                icon="mdi-package-variant"
                :class="$style.packageTableIcon"
              ></v-icon>
              <div :class="$style.packageTableText">Carton</div>
            </div>
          </div>
          <div
            ref="dropPallet"
            :class="$style.packageTableItem"
            @dragover.prevent="handleTableDragover($event, 'pallet')"
            @dragleave.prevent="handleTableDragleave($event, 'pallet')"
            @drop.prevent="handleTableDrop($event, 'pallet')"
          >
            <div
              v-if="palletPackingLegProduct"
              :class="$style.packageTableItemEdit"
            >
              <div :class="$style.packageTableEditItemHeader">
                <ul class="m-0 p-0">
                  <NewAirRoadUnpackedProductsBasicListItem
                    :close="removePalletPackingLegProduct"
                    :product="palletPackingLegProduct"
                    :is-quantity="false"
                  />
                </ul>
              </div>
              <NewAirRoadPackageTableForm
                :class="$style.packageTableForm"
                submit-text="Pack in pallet box"
                :max-quantity="palletPackingLegProduct.quantity"
                :initial-data="initialPalletData"
                @submit="
                  (event, formData) =>
                    handlePackageSubmit(
                      formData,
                      palletPackingLegProduct,
                      'pallet',
                    )
                "
              />
            </div>
            <div v-else :class="$style.packageTableItemEmpty">
              <v-icon
                icon="mdi-shipping-pallet"
                :class="$style.packageTableIcon"
              ></v-icon>
              <div :class="$style.packageTableText">Pallet</div>
            </div>
          </div>
        </div>
        <h3>Packaged products</h3>
        <div :class="$style.packagedProducts">
          <template v-if="model.data.items.length">
            <ul :class="$style.productsPackagesList">
              <NewAirRoadPackagesBasicListItem
                v-for="(productsPackage, index) in model.data.items"
                :key="index"
                v-model="model.data.items[index]"
                :package-num="index + 1"
                :class="$style.productsPackage"
                :pickup-products="allLegProducts"
                @package-unpack="handlePackageUnpack"
                @dragover.prevent="handlePackageDragover"
                @dragleave.prevent="handlePackageDragleave"
                @drop.prevent="handlePackageDrop"
              />
            </ul>
          </template>
          <template v-else>
            <div :class="$style.emptyText">Nothing is packed yet</div>
          </template>
        </div>
        <section v-if="model.data.items.length">
          <H3LineNewAirRoadHeader>Total</H3LineNewAirRoadHeader>
          <TotalPackageStepNewAirRoadCustomTable
            :data="packageNewAirRoadStepTotalTableData"
          />
        </section>
      </div>
    </div>
  </section>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.packageStepNewAirRoadSection {
}
  .emptyText {
    background-color: colors.$color-gray-50;
    min-height: 85px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .packaging {
    display: flex;
  }
    .leftPackaging {
      width: 275px;
    }
      .h3AndBtns {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .productsToPackItems {
        list-style: none;
        margin: 0;
        padding: 0;
      }
        .productsToPackItem {
          margin: 1em 0;
          cursor: grab;
        }
      .addingExtraProduct {
        margin: 1em 0;
      }
    .middlePackaging {
      margin: 18.72px auto;
      padding: 0 1.5rem;
    }
      .middlePackagingIcon {
        font-size: 26px;
        transform: rotate(-45deg);
      }
    .rightPackaging {
      width: 800px;
    }
      .packageTableItems {
        display: flex;
        gap: 40px;
      }
        .packageTableItem {
          width: 50%;
        }
        .isDragging .packageTableItem * {
          pointer-events: none;
        }
          .packageTableItem .packageTableItemEmpty,
          .packageTableItem .packageTableItemEdit {
            border-radius: 4px;
            min-width: 150px;
            min-height: 400px;
          }
          .packageTableItem.dragover .packageTableItemEmpty,
          .packageTableItem.dragover .packageTableItemEdit {
            border: 1px solid colors.$color-gray-300;
          }
          .packageTableItemEmpty {
            background-color: colors.$color-gray-50;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
            .packageTableIcon {
              color: colors.$color-gray-200;
              font-size: 102px;
            }
            .packageTableText {
              color: colors.$color-text-tertiary;
              font-weight: 500;
              font-size: 18px;
              margin-top: 8px;
            }
          .packageTableItemEdit {
            border: 1px solid colors.$color-gray-200;
          }
            .packageTableEditItemHeader {
              margin: -1px;
            }
            .packageTableForm {
              padding: 16px;
            }
      .packagedProducts {
        background-color: colors.$color-gray-50;
        border-radius: 4px;
        min-height: 85px;
        width: 100%;
        padding: 1em;
      }
        .productsPackagesList {
          margin: 0;
          padding: 0;
          display: flex;
          align-items: start;
          flex-wrap: wrap;
          gap: 16px;
        }
          .productsPackage {
            width: calc(50% - 8px);
          }
          .isDragging .productsPackage * {
            pointer-events: none;
          }
          .productsPackage.dragover {
            border: 1px solid colors.$color-gray-300;
          }
</style>
